import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Globe, ImagePlus, Loader2, MoreHorizontal } from 'lucide-react'
import { toast } from 'sonner'
import { getDocument } from '@/lib/api/documents'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
} from '@/lib/menu-chrome'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

export type ImageSearchHit = {
  title: string
  image_url: string
  source_url?: string | null
  thumbnail_url?: string | null
  width?: number
  height?: number
}

function buildImageMarkdown(hit: ImageSearchHit): string {
  const alt = (hit.title || 'Diagram').replace(/[[\]]/g, '')
  const src = hit.image_url
  const source = hit.source_url
  const lines = [`![${alt}](${src})`, '']
  if (source) {
    lines.push(`Source: [${alt}](${source})`)
  } else {
    lines.push(`Source: ${src}`)
  }
  return lines.join('\n')
}

const INSERT_NEEDS_DOCUMENT_HINT = 'Open a document to insert'

function resolveActiveDocumentId(): string | null {
  try {
    const fromWindow = (window as unknown as { __flownotesActiveDocumentId?: string })
      .__flownotesActiveDocumentId
    if (typeof fromWindow === 'string' && fromWindow.trim()) return fromWindow.trim()
  } catch {
    /* ignore */
  }
  return null
}

/** Prop first; window helper stays live when ToolMessage does not remount on tab change. */
function useResolvedActiveDocumentId(preferred?: string | null): string | null {
  const [windowId, setWindowId] = useState(resolveActiveDocumentId)
  useEffect(() => {
    const sync = () => setWindowId(resolveActiveDocumentId())
    sync()
    const id = window.setInterval(sync, 1000)
    return () => window.clearInterval(id)
  }, [])
  const fromProp = typeof preferred === 'string' ? preferred.trim() : ''
  return fromProp || windowId
}

function DisabledInsertHint({ children }: { children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block w-full cursor-not-allowed">{children}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {INSERT_NEEDS_DOCUMENT_HINT}
      </TooltipContent>
    </Tooltip>
  )
}

interface ImageSearchGalleryProps {
  query?: string
  results: ImageSearchHit[]
  /** Preferred document id (open tab). Falls back to window helper / event without id. */
  activeDocumentId?: string | null
}

function ImageSearchCardMenu({
  index,
  sourceUrl,
  inserting,
  insertDisabled,
  busy,
  onInsert,
}: {
  index: number
  sourceUrl?: string | null
  inserting: boolean
  insertDisabled: boolean
  busy: boolean
  onInsert: () => void
}) {
  const insertItem = (
    <DropdownMenuItem
      disabled={insertDisabled}
      onClick={onInsert}
      data-testid={testIds.imageSearchGallery.insertMenuItem(index)}
    >
      {inserting ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <ImagePlus className="h-4 w-4" />
      )}
      Insert into document
    </DropdownMenuItem>
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
          disabled={busy}
          aria-label="Image actions"
          data-testid={testIds.imageSearchGallery.overflowMenu(index)}
        >
          {inserting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <MoreHorizontal className="h-3.5 w-3.5" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
      >
        {insertDisabled && !inserting ? (
          <DisabledInsertHint>{insertItem}</DisabledInsertHint>
        ) : (
          insertItem
        )}
        {sourceUrl ? (
          <DropdownMenuItem asChild>
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={testIds.imageSearchGallery.sourceMenuItem(index)}
            >
              <Globe className="h-4 w-4" />
              Open source
            </a>
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function ImageSearchGallery({
  query,
  results,
  activeDocumentId,
}: ImageSearchGalleryProps) {
  const [insertingIndex, setInsertingIndex] = useState<number | null>(null)
  const resolvedDocumentId = useResolvedActiveDocumentId(activeDocumentId)
  const canInsert = Boolean(resolvedDocumentId)

  const handleInsert = useCallback(
    async (hit: ImageSearchHit, index: number) => {
      if (!hit.image_url || !/^https:\/\//i.test(hit.image_url)) {
        toast.error('Only https image URLs can be inserted')
        return
      }
      const documentId = resolvedDocumentId
      if (!documentId) {
        toast.error(INSERT_NEEDS_DOCUMENT_HINT)
        return
      }

      setInsertingIndex(index)
      try {
        const doc = await getDocument(documentId)
        const body = doc.content || ''
        const block = buildImageMarkdown(hit)
        const proposed = body.trimEnd() ? `${body.trimEnd()}\n\n${block}\n` : `${block}\n`
        window.dispatchEvent(
          new CustomEvent('flownotes:propose-document-edit', {
            detail: {
              type: 'proposed_edit',
              documentId: doc.id,
              filename: doc.filePath || doc.file_path || 'document.md',
              title: doc.title || 'Document',
              original: body,
              proposed,
              explanation: `Insert diagram from web image search${query ? `: ${query}` : ''}`,
              requiresAcceptance: true,
            },
          })
        )
        toast.success('Proposed image insert - accept the edit to apply')
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to prepare insert')
      } finally {
        setInsertingIndex(null)
      }
    },
    [resolvedDocumentId, query]
  )

  if (!results.length) return null

  return (
    <div
      className="@container/image-gallery mt-2 space-y-2 not-prose"
      data-testid={testIds.imageSearchGallery.root}
    >
      <p className="text-xs text-muted-foreground">
        {results.length} image{results.length === 1 ? '' : 's'}
        {query ? ` for "${query}"` : ''}
        {canInsert
          ? ' - pick one to insert into the open document'
          : ` - ${INSERT_NEEDS_DOCUMENT_HINT.toLowerCase()}`}
      </p>
      <div className="grid grid-cols-2 @[640px]/image-gallery:grid-cols-3 gap-2">
        {results.map((hit, index) => {
          const thumb = hit.thumbnail_url || hit.image_url
          const inserting = insertingIndex === index
          const busy = insertingIndex !== null
          const insertDisabled = busy || !canInsert
          const insertButton = (
            <Button
              size="sm"
              variant="outline"
              className="w-full h-7 text-xs"
              disabled={insertDisabled}
              onClick={() => void handleInsert(hit, index)}
              data-testid={testIds.imageSearchGallery.insertButton(index)}
            >
              {inserting ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Preparing...
                </>
              ) : (
                'Insert into document'
              )}
            </Button>
          )
          return (
            <div
              key={`${hit.image_url}-${index}`}
              className={cn(
                'rounded-md border border-border bg-card overflow-hidden flex flex-col',
                'min-w-0'
              )}
              data-testid={testIds.imageSearchGallery.card(index)}
            >
              <a
                href={hit.image_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block aspect-[4/3] bg-muted overflow-hidden"
              >
                <img
                  src={thumb}
                  alt={hit.title || 'Search result'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </a>
              <div className="p-1.5 @[480px]/image-gallery:p-2 flex-1 flex flex-col gap-1 @[480px]/image-gallery:gap-1.5">
                <div className="flex items-start gap-0.5 min-w-0">
                  <p className="text-xs font-medium leading-snug min-w-0 flex-1 line-clamp-1 @[480px]/image-gallery:line-clamp-2">
                    {hit.title}
                  </p>
                  <div className="@[480px]/image-gallery:hidden -mt-0.5 -mr-0.5">
                    <ImageSearchCardMenu
                      index={index}
                      sourceUrl={hit.source_url}
                      inserting={inserting}
                      insertDisabled={insertDisabled}
                      busy={busy}
                      onInsert={() => void handleInsert(hit, index)}
                    />
                  </div>
                </div>
                <div className="hidden @[480px]/image-gallery:flex flex-col gap-1.5 mt-auto">
                  {hit.source_url && (
                    <a
                      href={hit.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 truncate"
                      data-testid={testIds.imageSearchGallery.sourceLink(index)}
                    >
                      <Globe className="h-3 w-3 shrink-0" />
                      <span className="truncate">Source</span>
                    </a>
                  )}
                  {!canInsert && !inserting ? (
                    <DisabledInsertHint>{insertButton}</DisabledInsertHint>
                  ) : (
                    insertButton
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
